import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Select, Spinner } from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { employees, getEmployeeName } from "../../Config/Employees";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../components/Hrms/HrmsCard";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import { LEAVE_TYPE_META, leaveTypeColor, PersonCell } from "../../components/Hrms/hrmsUi";

interface EmployeeLeaveSummary {
  email: string;
  name: string;
  byType: Record<string, number>;
}

export const LeaveReports = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const { canViewReports } = getUserAccess();

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [month, setMonth] = useState(DateTime.now().toFormat("MM"));
  const [year, setYear] = useState(DateTime.now().year.toString());
  const [selectedEmail, setSelectedEmail] = useState("");
  const [rows, setRows] = useState<EmployeeLeaveSummary[]>([]);

  const leaveTypes = Array.from(
    new Set(rows.flatMap((row) => Object.keys(row.byType))),
  );

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getLeaveReport(
        token,
        month,
        year,
        userEmail,
        selectedEmail,
      );
      if (res.data.status === "success" && res.data.data) {
        setRows(res.data.data.employees || []);
      }
    } catch (e: any) {
      console.error("Error fetching leave report:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await EmployeeDataService.exportLeaveReport(
        token,
        month,
        year,
        userEmail,
        selectedEmail,
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leave-report-${month}-${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) {
      console.error("Error exporting leave report:", e);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HrmsPageShell
      title="Leave Reports"
      description="Approved leave days per employee and leave type"
      sectionKey="reports"
      noPermission={!canViewReports}
    >
      <HrmsCard mb={4}>
        <Flex gap={4} wrap="wrap" align="flex-end">
          {canViewReports && (
            <Select
              placeholder="All Employees"
              w="220px"
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
            >
              {employees.map((emp) => (
                <option key={emp.value} value={emp.value}>
                  {emp.label}
                </option>
              ))}
            </Select>
          )}
          <Select
            w="140px"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {Array.from({ length: 12 }, (_, i) =>
              String(i + 1).padStart(2, "0"),
            ).map((m) => (
              <option key={m} value={m}>
                {DateTime.fromObject({ month: Number(m) }).toFormat("MMMM")}
              </option>
            ))}
          </Select>
          <Select w="120px" value={year} onChange={(e) => setYear(e.target.value)}>
            {[year, String(Number(year) - 1)].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
          <Button
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={fetchReport}
          >
            Generate
          </Button>
          <Button
            variant="outline"
            borderColor="#327171"
            color="#327171"
            onClick={handleExport}
            isLoading={isExporting}
          >
            Export to Excel
          </Button>
        </Flex>
      </HrmsCard>

      {isLoading ? (
        <Flex justify="center" align="center" h="40vh">
          <Spinner size="xl" color="#308888" />
        </Flex>
      ) : (
        <HrmsTable
          head={[
            "Employee",
            ...leaveTypes.map((type) => (
              <Flex key={type} align="center" gap={1.5}>
                <Box w="8px" h="8px" borderRadius="full" bg={`${leaveTypeColor(type)}.400`} />
                {LEAVE_TYPE_META[type]?.label || type}
              </Flex>
            )),
            "Total",
          ]}
          isEmpty={rows.length === 0}
          emptyText="No approved leaves for this period"
        >
          {rows.map((row) => (
            <tr key={row.email}>
              <td>
                <PersonCell
                  name={row.name || getEmployeeName(row.email) || row.email}
                  subtitle={row.email}
                />
              </td>
              {leaveTypes.map((type) => (
                <td key={type} style={{ color: row.byType[type] ? undefined : "#a0aec0", fontWeight: row.byType[type] ? 600 : 400 }}>
                  {row.byType[type] || 0}
                </td>
              ))}
              <td style={{ fontWeight: 700 }}>
                {leaveTypes.reduce((sum, type) => sum + (row.byType[type] || 0), 0)}
              </td>
            </tr>
          ))}
        </HrmsTable>
      )}
    </HrmsPageShell>
  );
};
